// @file Surface comment attachments store
import { vDel, vSet } from '@@/bits/vue'
import { CommentAttachmentState } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import type { Cid } from '@@/types'
import type { CommentCid, CommentId } from '@@/types/surface_comments'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceCommentAttachmentsStore = defineStore('surfaceCommentAttachments', () => {
  const contentPickerStore = useContentPickerStore()
  const surfaceStore = useSurfaceStore()
  const commentsStore = useCommentsStore()

  /* ---------------------- */
  /* COMMENT CONTENT PICKER */
  /* ---------------------- */

  const commentContentPickerDisabled = computed<boolean>(() => {
    // Disabled for Nord Anglia (tenant or library), custom request in PRO-15718
    const isNordAngliaTenant = surfaceStore.tenantId === 151209
    const isNordAngliaLibrary = surfaceStore.libraryId === 366889
    return isNordAngliaTenant || isNordAngliaLibrary
  })

  /**
   * Opens the content picker for a comment attachment.
   * @param commentCid - The CommentCid of the draft (caller must ensure draft exists)
   */
  const openCommentContentPicker = (commentCid: CommentCid): void => {
    contentPickerStore.showContentSourceMenu({
      destinationEntityType: 'Comment',
      destinationEntityUid: commentCid,
    })
  }

  const resetCommentContentPicker = (): void => {
    contentPickerStore.resetContentPicker()
  }

  /* ------------------------ */
  /* COMMENT ATTACHMENT STATE */
  /* ------------------------ */

  // #region New comment attachment state
  const updateNewCommentAttachmentState = ({
    postCid,
    state,
  }: {
    postCid: Cid
    state: CommentAttachmentState
  }): void => {
    commentsStore.updateNewCommentByPostCid(postCid, { attachmentState: state })
  }

  const removeNewCommentAttachmentState = ({ postCid }: { postCid: Cid }): void => {
    commentsStore.deleteNewCommentProperties(postCid, ['attachmentState'])
  }

  const newCommentAttachmentStateForPostCid = (postCid: Cid): CommentAttachmentState | undefined => {
    return commentsStore.newCommentByPostCid[postCid]?.attachmentState
  }

  // #endregion

  // #region Comment attachment video trimming upload
  // Record whether we're uploading and trimming a video for a new comment, as opposed to just uploading.
  const isUploadingAndTrimmingVideoInNewComment = (postCid: Cid): boolean => {
    const uploadJob = commentsStore.newCommentUploadJobForPostCid(postCid)
    if (uploadJob == null) return false
    return uploadJob.shouldTrimVideoAfterUpload
  }
  const isUploadingAndTrimmingVideoInEditedComment = (commentId: CommentId): boolean => {
    const uploadJob = commentsStore.editedDraftCommentUploadJobFromId(commentId)
    if (uploadJob == null) return false
    return uploadJob.shouldTrimVideoAfterUpload
  }
  // #endregion

  // #region Edited comment attachment state
  const updateEditedCommentAttachmentState = ({
    commentId,
    state,
  }: {
    commentId: CommentId
    state: CommentAttachmentState
  }): void => {
    commentsStore.updateEditedCommentById(commentId, { attachmentState: state })
  }

  const removeEditedCommentAttachmentState = ({ commentId }: { commentId: CommentId }): void => {
    commentsStore.deleteEditedCommentProperties(commentId, ['attachmentState'])
  }

  const editedCommentAttachmentStateForId = (commentId: CommentId): CommentAttachmentState => {
    return commentsStore.draftsForEditedCommentsById[commentId]?.attachmentState ?? CommentAttachmentState.IDLE
  }
  // #endregion

  /* ------------------------------ */
  /* DRAGGING FILE TO COMMENT STATE */
  /* ------------------------------ */
  const isDraggingFileToNewCommentByPostCid = ref<Record<Cid, true>>({})

  const startDraggingFileToNewComment = (postCid: Cid): void => {
    vSet(isDraggingFileToNewCommentByPostCid.value, postCid, true)
  }

  const stopDraggingFileToNewComment = (postCid: Cid): void => {
    vDel(isDraggingFileToNewCommentByPostCid.value, postCid)
  }

  return {
    // Comment content picker
    commentContentPickerDisabled,
    openCommentContentPicker,
    resetCommentContentPicker,

    // Comment attachment state
    updateNewCommentAttachmentState,
    removeNewCommentAttachmentState,
    newCommentAttachmentStateForPostCid,
    updateEditedCommentAttachmentState,
    removeEditedCommentAttachmentState,
    editedCommentAttachmentStateForId,

    // Comment attachment video trimming upload
    isUploadingAndTrimmingVideoInNewComment,
    isUploadingAndTrimmingVideoInEditedComment,

    // Dragging file to comment state
    isDraggingFileToNewCommentByPostCid,
    startDraggingFileToNewComment,
    stopDraggingFileToNewComment,
  }
})

export { CommentAttachmentState }
